import { useChatbotStore, useChatStateStore, useMessagesStore, useProductsStore, useUserStore, useToastStore } from "../zustand/store";
import { ChatBubble, ChatStreamingBubble, GreetingBubble, LoadingBubble, TailBubble } from "./ChatBubble";
import { useEffect, useMemo, useRef, useState } from 'react';
// import PreQuestion from "./PreQuestion";
import { CollectionForm, CompletedForm } from "./Form";
import Toast from "./Toast";
import PreQuestionRefactored from "./PreQuestion/PreQuestionRefactored";
import FastfiveEntryView from "./Common/FastfiveEntryView";

const Chat = () => {
    const { messages } = useMessagesStore();
    const { chatbot } = useChatbotStore();
    const { partnerType } = useUserStore();
    const { followupQuestion } = useProductsStore();
    const {
        isChatbotPending,
        isPreAnswered,
        isFormSubmitted,
        setIsFormSubmitted,
        isStreaming,
        displayLocation,
        isLastIntentProductRecommendation,
        fastfiveScenarioId,
        isScenarioAutoEntered,
    } = useChatStateStore();

    // fastfive 시나리오 모드: chatbot.scenarios 배열이 내려오면 entry 버튼 기반 분기.
    const hasFastfiveScenarios = partnerType === 'fastfive'
        && Array.isArray(chatbot?.scenarios) && chatbot.scenarios.length > 0;
    const selectedScenario = useMemo(() => {
        if (!hasFastfiveScenarios) return null;
        const found = chatbot.scenarios.find(s => s.key === fastfiveScenarioId);
        // 다음 두 경우는 "자동 진입 대상 아님" → null 처리해서 entry 버튼이 노출되도록:
        //   1) 찾지 못함 (잘못된 키 또는 lounge 처럼 미지정)
        //   2) flow === 'redirect' (PreQuestion 이 없는 시나리오는 자동 진입할 게 없음;
        //      사용자가 entry 버튼에서 직접 누르면 그때 redirect 동작)
        if (!found || found.flow === 'redirect') return null;
        return found;
    }, [hasFastfiveScenarios, chatbot?.scenarios, fastfiveScenarioId]);

    // PreQuestion 에 넘길 config 결정: 시나리오 모드면 선택 시나리오, 아니면 레거시(chatbot.preQuestion).
    const effectivePreQuestion = hasFastfiveScenarios
        ? selectedScenario?.preQuestion
        : chatbot?.preQuestion;
    const hasPreQuestion = Array.isArray(effectivePreQuestion?.questions)
        && effectivePreQuestion.questions.length > 0;
    const { isToastVisible } = useToastStore();
    const chatContainerRef = useRef(null);
    const [isTailVisible, setIsTailVisible] = useState(false);
    const [isAutoScrolling, setIsAutoScrolling] = useState(true);

    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;

            // overscroll prevention (even when content height is less than container height)
            chatContainerRef.current.addEventListener('wheel', (e) => {
                const atTop = chatContainerRef.current.scrollTop === 0;
                const atBottom = chatContainerRef.current.scrollHeight - chatContainerRef.current.scrollTop === chatContainerRef.current.clientHeight;

                const scrollingUp = e.deltaY < 0;
                const scrollingDown = e.deltaY > 0;

                const prevent =
                    (atTop && scrollingUp) || (atBottom && scrollingDown);

                if (prevent) {
                    e.preventDefault();
                }
            }, { passive: false })

            let startY = 0;

            chatContainerRef.current.addEventListener('touchstart', (e) => {
                startY = e.touches[0].clientY;
            }, { passive: true });

            chatContainerRef.current.addEventListener('touchmove', (e) => {
                const currentY = e.touches[0].clientY;
                const deltaY = currentY - startY;

                const atTop = chatContainerRef.current.scrollTop === 0;
                const atBottom = chatContainerRef.current.scrollHeight - chatContainerRef.current.scrollTop === chatContainerRef.current.clientHeight;

                const scrollingUp = deltaY > 0;
                const scrollingDown = deltaY < 0;

                const prevent =
                    (atTop && scrollingUp) || (atBottom && scrollingDown);

                if (prevent) {
                    e.preventDefault();
                }
            }, { passive: false });
        }
    }, [messages, chatbot?.preQuestion, isTailVisible, isFormSubmitted, followupQuestion]);

    useEffect(() => {
        const fastfiveCondition = (partnerType === 'fastfive') && messages.length > 0 && !isChatbotPending && isPreAnswered && isFormSubmitted !== 'submitted';
        const otherCondition = messages.length > 0 && !isChatbotPending && !isStreaming;
        const condition = partnerType === 'fastfive' ? fastfiveCondition : otherCondition;

        if (condition) {
            setIsTailVisible(true);
        } else {
            setIsTailVisible(false);
        }
    }, [followupQuestion, messages, isChatbotPending, isPreAnswered, isStreaming, isFormSubmitted]);

    useEffect(() => {
        if (isStreaming && !isChatbotPending) {
            setIsAutoScrolling(true);
        }
    }, [isStreaming, isChatbotPending]);


    useEffect(() => {
        if (!chatContainerRef.current) return;
        const currentChatContainerRef = chatContainerRef.current;

        const handleWheel = (e) => {
            if (isAutoScrolling) {
                setIsAutoScrolling(false);
            }
        };

        const handleTouchMove = (e) => {
            if (isAutoScrolling) {
                setIsAutoScrolling(false);
            }
        };

        if (isStreaming && !isChatbotPending && isAutoScrolling) {
            chatContainerRef.current.addEventListener('wheel', handleWheel, { passive: false });
            chatContainerRef.current.addEventListener('touchmove', handleTouchMove, { passive: false });
        }

        return () => {
            if (currentChatContainerRef) {
                currentChatContainerRef.removeEventListener('wheel', handleWheel, { passive: false });
                currentChatContainerRef.removeEventListener('touchmove', handleTouchMove, { passive: false });
            }
        };
    }, [isStreaming, isChatbotPending, isAutoScrolling]);

    useEffect(() => {
        if (hasPreQuestion && !isPreAnswered) return;
        if (partnerType === 'fastfive') {
            // formOnly flow는 PreQuestionRefactored 가 직접 폼을 열기 때문에 여기서는 처리하지 않음.
            if (!isLastIntentProductRecommendation) return;
        }
        if (partnerType !== 'fastfive' && !chatbot.experimentalData?.some(data => data.key === 'csInquiry')) return;
        if (messages.length > 0
            && isTailVisible
            && followupQuestion.length > 0) {
            setTimeout(() => {
                setIsFormSubmitted('ready');
            }, 2000);
        }

    }, [isTailVisible, followupQuestion, isLastIntentProductRecommendation, partnerType, hasPreQuestion, isPreAnswered]);

    // fastfive 시나리오 모드의 entry 화면: 시나리오 선택 전이고 진행 흔적이 없으면 별도 뷰로 분기.
    // - 저장된 scenarioId 가 현재 metadata 에 없으면(selectedScenario === null) 자동으로 entry 가 다시 노출됨.
    // - messages 조건은 유지해야 한다. 빼면 대화가 복원된 방문자 위로 entry 화면이 덮이는데,
    //   그 상태는 isPreAnswered=true 라서 사전질문이 건너뛰어지고 ChatInput 도 살아있다.
    const showEntryView = hasFastfiveScenarios && !selectedScenario
        && messages.length === 0 && !isChatbotPending
        && displayLocation !== 'PRODUCT_DETAIL';

    if (showEntryView) {
        return <FastfiveEntryView />;
    }

    return (
        <div
            ref={chatContainerRef}
            style={{ width: '100%', flex: 1, position: 'relative', overflow: 'scroll', padding: '8px 0', overscrollBehavior: 'contain' }}
        >
            {
                // scenario flow 가 아닐 때(시나리오 미선택)만 메인 greeting 을 사용.
                // scenario flow 진입 후엔 아래 블록의 scenario.greetingMessage 가 사용됨.
                messages.length === 0 && !isChatbotPending && displayLocation !== 'PRODUCT_DETAIL'
                && !selectedScenario &&
                <GreetingBubble />
            }
            {
                // scenario flow: 선택 시나리오의 greetingMessage 를 챗봇 버블로 노출.
                // - redirect 시나리오(lounge)는 selectedScenario 가 null 처리되므로 자동으로 노출되지 않음.
                // - isScenarioAutoEntered 가드: 도메인 이동(URL/SDK) 진입 시에만 노출, entry 버튼 클릭으로
                //   들어온 경우에는 숨김 (버튼을 이미 눌러서 컨텍스트가 명확하므로 반복 안내 불필요).
                messages.length === 0 && !isChatbotPending && displayLocation !== 'PRODUCT_DETAIL'
                && isScenarioAutoEntered && selectedScenario?.greetingMessage &&
                <ChatBubble message={{ message: selectedScenario.greetingMessage, isChatbot: true }} />
            }
            {
                messages.length === 0 && !isChatbotPending && displayLocation === 'PRODUCT_DETAIL' &&
                <LoadingBubble />
            }
            {
                messages.length > 0 &&
                messages?.map((message, index) => (
                    message.type === 'form' ?
                        <CompletedForm
                            formData={message.message}
                            chatContainerRef={chatContainerRef}
                        /> :
                        <ChatBubble key={index} message={message}
                        />
                ))
            }
            {
                isStreaming && !isChatbotPending &&
                <ChatStreamingBubble
                    chatContainerRef={chatContainerRef}
                    isAutoScrolling={isAutoScrolling}
                />
            }
            {
                isChatbotPending &&
                <LoadingBubble />
            }
            {
                // 시나리오 모드면 선택 시나리오의 preQuestion 을, 아니면 레거시 chatbot.preQuestion 을 사용.
                hasPreQuestion && (!hasFastfiveScenarios || selectedScenario) &&
                <PreQuestionRefactored
                    config={hasFastfiveScenarios ? effectivePreQuestion : null}
                    flow={hasFastfiveScenarios ? selectedScenario?.flow : null}
                />
            }
            {/* {
                partnerType !== 'fastfive' && messages.length === 0 &&
                <GuideButtonGroup />
            } */}
            {
                isTailVisible &&
                <TailBubble message={followupQuestion} />
            }
            {
                isFormSubmitted === 'ready' &&
                <>
                    {selectedScenario?.formIntroMessage &&
                        <ChatBubble
                            message={{ message: selectedScenario.formIntroMessage, isChatbot: true }}
                        />
                    }
                    <CollectionForm
                        chatContainerRef={chatContainerRef}
                    />
                </>
            }
            <Toast />
        </div>
    )
}

export default Chat;
